/**
 * Design tokens Repère — source de vérité : docs/01-produit.md §3.
 *
 * Le bleu et le rouge sont ceux du drapeau français (#002395 et #ED2939). Repère est un
 * produit français, adossé au droit français, et cette filiation est un signal de sérieux
 * pour un parent comme pour un avocat.
 *
 * **Le rouge est décoratif, jamais sémantique.** Il marque l'identité — un filet, un
 * accent — et ne dit jamais qu'une chose ne va pas : les alertes restent à l'ambre
 * (docs/01 §3). Un produit conçu pour apaiser ne peut pas se mettre à crier en rouge sur
 * des parents en conflit. Il ne porte pas non plus de texte sur fond clair : 4:1 avec le
 * blanc, sous le seuil AA — d'où `redInk`, sa déclinaison lisible.
 */
export const color = {
    navy: '#20418F',
    /**
     * Le même bleu, mais dans son rôle de **texte** — un onglet actif, un lien, le
     * mot-symbole. En clair les deux valeurs coïncident ; en sombre elles divergent, et
     * c'est toute la raison de les avoir séparées : un aplat de marque doit rester sombre
     * pour porter du blanc, tandis qu'un texte de marque doit s'éclaircir pour se détacher
     * du fond. Une seule valeur ne peut pas faire les deux sans qu'un des deux emplois
     * devienne illisible.
     */
    navyInk: '#20418F',
    navyDeep: '#17306A',
    /**
     * Le texte posé **sur** un aplat de marque — l'intitulé d'un bouton principal, le compte
     * d'une pastille, le bandeau. Il vaut blanc dans les deux thèmes, parce que l'aplat reste
     * sombre dans les deux.
     *
     * Il existe parce que ces textes empruntaient `surface`, en lui faisant dire « blanc »
     * alors qu'elle dit « le fond d'une carte ». Tant que ce fond était blanc, la confusion
     * ne coûtait rien ; au premier thème sombre elle a écrit en anthracite sur du marine.
     */
    onNavy: '#FFFFFF',
    /**
     * Le bleu du drapeau français, et rien d'autre.
     *
     * Il ne sert qu'au filet tricolore. Le bleu de l'interface s'en est détaché — il est
     * désormais plus clair — mais le filet, lui, cite un drapeau : il n'a de sens que s'il
     * en porte la couleur exacte. D'où un token à part plutôt qu'un alias de `navy` : le
     * jour où le bleu de marque bougera encore, le drapeau ne doit pas bouger avec lui.
     */
    flagBlue: '#002395',
    /**
     * Le blanc du drapeau — et il vaut `#FFFFFF` dans les deux thèmes, comme `flagBlue`.
     *
     * Le filet citait jusqu'ici `surface` pour sa bande centrale. En clair, `surface` **est**
     * blanc : le rendu était juste, et la bande se lisait comme le blanc d'un drapeau imprimé
     * sur du papier blanc. En sombre, `surface` est presque noir — le drapeau n'avait plus que
     * deux couleurs, et le bleu touchait presque le rouge, contre lequel il ne se détache pas
     * (2,97:1, voir le test de palette).
     *
     * Un drapeau ne suit pas le thème de l'interface : c'est la même raison qui a détaché
     * `flagBlue` du bleu de marque.
     */
    flagWhite: '#FFFFFF',
    red: '#ED2939',
    redInk: '#B3001B',
    redSurface: '#FDECEE',
    sage: '#8FAE9B',
    /**
     * Le vert du logo, plus clair que le sage d'interface : c'est la valeur exacte relevée
     * sur le fichier de marque, et le pictogramme la reprend telle quelle. Elle ne porte
     * jamais de texte — 1,86:1 sur blanc — d'où sa cohabitation avec `sage`, qui reste la
     * teinte lisible.
     */
    sageLogo: '#A6C6AC',
    /**
     * Le vert lisible — même rôle que `redInk` pour le rouge. Ni `sage` (2,4:1) ni
     * `sageLogo` (1,9:1) ne tiennent comme texte sur blanc ; celui-ci atteint 5,9:1 et
     * porte le suffixe de la marque partout où le fond est clair.
     */
    sageInk: '#4C6B58',
    ink: '#16212B',
    muted: '#5B6B7A',
    border: '#DCE3E9',
    surface: '#FFFFFF',
    background: '#F6F8FA',
    amber: '#B7791F',
    amberSurface: '#FEF6E7',
    success: '#3F7D58',
    /**
     * Le vert d'un état, sur son fond teinté — la paire qui manquait, alors que l'ambre et
     * le rouge avaient déjà la leur. `success` seul tombe à 3,8:1 posé sur un aplat vert :
     * assez pour un pictogramme, pas pour le texte d'une pastille.
     *
     * Ces deux valeurs sont celles que la vitrine emploie déjà pour son badge vert : deux
     * verts d'état différents entre le site et l'application n'auraient rien apporté.
     */
    successInk: '#2F6146',
    successSurface: '#E9F1EC',
    /**
     * Les quatre teintes du langage graphique (voir `iconPalette` plus bas). Elles sont dans
     * `color` parce qu'elles deviennent des variables CSS comme les autres, mais elles ne
     * servent qu'à remplir des formes : jamais un fond de texte, jamais un état.
     */
    iconBlue: '#D2E3F7',
    iconGreen: '#D9E7D6',
    iconDeepBlue: '#3D5573',
    iconDeepGreen: '#6E8F7C',
    /**
     * Les deux fonds du calendrier de garde — « chez vous » et « chez l'autre parent ».
     *
     * Ils se distinguent par la **clarté** avant la teinte, et c'est délibéré. La première
     * version employait deux pastels de même luminosité, l'un bleuté l'autre verdi : à
     * l'écran, l'alternance des semaines disparaissait, et pour un daltonien il n'y avait
     * jamais rien eu à voir. Un écart de clarté se lit sans distinguer les couleurs, et un
     * calendrier de garde est précisément l'écran où se tromper de semaine coûte cher.
     *
     * L'écart est mesuré par un test. Le plafond, lui, est la lisibilité du numéro du jour :
     * les deux fonds gardent le texte d'encre bien au-delà de 4,5:1.
     */
    custodySelf: '#E3EBFA',
    custodyOther: '#C2D8C9',
};
/**
 * Langage des pictogrammes — relevé sur la planche d'icônes de la marque.
 *
 * Trois règles, et elles suffisent à reconnaître une icône Repère :
 *
 * - **Des formes pleines, jamais de contour.** Le trait fin est le langage des outils de
 *   productivité ; la surface pleine et arrondie est celui de l'enfance. Repère parle des
 *   enfants de gens qui traversent une séparation — c'est le second ton qu'il lui faut.
 * - **Deux teintes par icône**, le bleu et le vert, l'une portant la forme principale et
 *   l'autre l'accessoire. Une icône monochrome perdrait la parenté avec le logo, qui est
 *   lui-même deux formes de deux couleurs.
 * - **Le détail se creuse en négatif**, il ne se dessine pas : le visage d'une silhouette
 *   est un trou dans la forme, pas un trait posé dessus. C'est ce qui tient à 20 px.
 *
 * Les deux pastels ne se distinguent que par la teinte — 1,02:1 l'un contre l'autre. Une
 * icône ne doit donc jamais faire porter un sens à celle de ses deux couleurs qui remplit
 * une forme : c'est la forme qui parle, la couleur ne fait que l'habiller. Un daltonien
 * voit les mêmes dessins, et ne perd rien.
 *
 * Les pastels ne conviennent qu'aux **grandes tailles décoratives** — un fond de bloc
 * vide, une illustration d'accueil. À 20 px dans une barre de navigation ils tombent à
 * 1,3:1 sur blanc et disparaissent : les mêmes formes y sont remplies de `deep`, mesuré
 * par un test. La forme voyage d'une taille à l'autre, pas la valeur.
 */
export const iconPalette = {
    /** Grandes tailles : illustrations, blocs vides, accueil. */
    pastel: { primaire: color.iconBlue, secondaire: color.iconGreen },
    /** Petites tailles : navigation, boutons, listes. */
    deep: { primaire: color.iconDeepBlue, secondaire: color.iconDeepGreen },
};
/**
 * Palette des avatars d'enfants — distincte de celle de l'interface, et c'est voulu.
 *
 * La palette de marque est faite pour du chrome : sobre, peu de teintes, beaucoup de bleu.
 * Posée sur l'avatar d'un enfant de six ans, elle donne un écran d'administration. Les
 * enfants choisissent leur couleur, et la leur refuser au nom d'une charte destinée à des
 * boutons serait une rigueur mal placée.
 *
 * Chaque entrée est une **paire** : un fond clair, un trait foncé. Le rapport entre les
 * deux est vérifié par un test — un trait pâle sur un fond pâle rendrait la silhouette
 * invisible, et c'est justement ce qui arrive quand on choisit des couleurs « douces » à
 * l'œil sans les mesurer.
 *
 * Le rouge du drapeau reste exclu : il est décoratif dans ce produit et jamais porteur de
 * sens, or un enfant marqué en rouge se lirait comme un signalement (docs/01 §3). Le rose
 * et le violet n'ont pas ce problème — personne n'a jamais signalé une alerte en rose.
 */
export const avatarColor = {
    navy: { fond: '#E4E9FA', trait: '#002395' },
    turquoise: { fond: '#DFF1F2', trait: '#12626B' },
    sage: { fond: '#EAF0EC', trait: '#4C6B58' },
    amber: { fond: '#FEF6E7', trait: '#8A5A0B' },
    rose: { fond: '#FDE9F1', trait: '#A3195B' },
    violet: { fond: '#F0E9FB', trait: '#5B2A97' },
    corail: { fond: '#FDECE7', trait: '#9C3A17' },
    muted: { fond: '#EAEEF2', trait: '#48555F' },
};
export const space = { xs: 4, sm: 8, md: 16, lg: 24, xl: 32, xxl: 48 };
export const radius = { sm: 6, md: 10, lg: 16, pill: 999 };
export const fontSize = { xs: 12, sm: 14, base: 16, lg: 20, xl: 26, xxl: 34 };
export const fontWeight = { regular: 400, medium: 500, semibold: 600, bold: 700 };
/**
 * Palette sombre — **mêmes clés, mêmes rôles**, d'autres valeurs.
 *
 * Ce n'est pas la palette claire inversée. Un thème sombre obtenu en retournant les
 * luminosités donne des aplats criards et des ombres inopérantes ; chaque valeur est ici
 * choisie puis mesurée contre les fonds sur lesquels elle se pose réellement, par les
 * mêmes tests que la palette claire.
 *
 * **Les avatars n'y figurent pas, et c'est délibéré.** Leur couleur est choisie par un
 * enfant ; la faire changer selon le thème de l'appareil du parent qui regarde reviendrait
 * à ce que la pastille de Chloé ne soit pas la même chez son père et chez sa mère. Leurs
 * fonds pâles restent lisibles sur une surface sombre, et le test le vérifie.
 */
export const darkColor = {
    navy: '#20418F',
    navyInk: '#A8C1F2',
    navyDeep: '#C2D4F7',
    onNavy: '#FFFFFF',
    flagBlue: '#002395',
    // Inchangé en sombre : un drapeau ne suit pas le thème. C'est tout l'objet du correctif.
    flagWhite: '#FFFFFF',
    red: '#ED2939',
    redInk: '#FF8A93',
    redSurface: '#3B1B21',
    sage: '#8FAE9B',
    sageLogo: '#A6C6AC',
    sageInk: '#9DC7AA',
    ink: '#E8EDF2',
    muted: '#9AA9B8',
    border: '#2F3B4A',
    surface: '#18212C',
    background: '#101720',
    amber: '#E3AC46',
    amberSurface: '#392D14',
    success: '#72C395',
    successInk: '#8FD3AC',
    successSurface: '#1D3328',
    iconBlue: '#24354C',
    iconGreen: '#25372C',
    iconDeepBlue: '#93A9C4',
    iconDeepGreen: '#95B6A3',
    custodySelf: '#20304A',
    custodyOther: '#2E4438',
};
/**
 * Variables CSS dérivées des tokens.
 *
 * Le site, l'application web et le mobile partent des mêmes valeurs : une palette
 * recopiée à trois endroits finit par diverger, et deux parents verraient alors deux
 * interfaces qui ne se ressemblent plus.
 *
 * `dark: true` ajoute les deux blocs du thème sombre. C'est un **choix explicite** et non
 * le défaut : une feuille qui ignore encore le sombre — la vitrine, dont les fonds sont
 * écrits en dur — se retrouverait à moitié assombrie, texte clair sur cartes claires. Le
 * jour où elle saura le porter, elle passera l'option à son tour.
 */
export const cssVariables = ({ dark = false } = {}) => {
    const couleurs = (palette) => Object.entries(palette)
        .map(([nom, valeur]) => `  --couleur-${kebab(nom)}: ${valeur};`)
        .join('\n');
    const lignes = [
        couleurs(color),
        ...Object.entries(space).map(([nom, valeur]) => `  --espace-${nom}: ${valeur}px;`),
        ...Object.entries(radius).map(([nom, valeur]) => `  --rayon-${nom}: ${valeur}px;`),
        ...Object.entries(fontSize).map(([nom, valeur]) => `  --taille-${nom}: ${valeur}px;`),
        ...Object.entries(fontWeight).map(([nom, valeur]) => `  --graisse-${nom}: ${valeur};`),
        ...Object.entries(avatarColor).flatMap(([nom, paire]) => [
            `  --avatar-${kebab(nom)}-fond: ${paire.fond};`,
            `  --avatar-${kebab(nom)}-trait: ${paire.trait};`,
        ]),
    ];
    const clair = `:root {\n${lignes.join('\n')}\n}`;
    if (!dark)
        return clair;
    // Trois états, et il en faut bien trois. « Auto » ne pose aucun attribut et suit le
    // système ; un choix explicite pose `data-theme` et doit l'emporter **dans les deux
    // sens** — d'où le `:not([data-theme='light'])` qui laisse le mode clair forcé résister
    // à un système en sombre, et le bloc `[data-theme='dark']` qui fait l'inverse.
    const sombre = couleurs(darkColor);
    return [
        clair,
        `@media (prefers-color-scheme: dark) {\n  :root:not([data-theme='light']) {\n${indenter(sombre)}\n  }\n}`,
        `:root[data-theme='dark'] {\n${sombre}\n}`,
    ].join('\n\n');
};
const indenter = (bloc) => bloc
    .split('\n')
    .map((ligne) => `  ${ligne}`)
    .join('\n');
const kebab = (valeur) => valeur.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
/** Ratio de contraste entre deux couleurs hexadécimales (WCAG 2.1). */
export function contrastRatio(avant, arriere) {
    const luminance = (hex) => {
        const canal = (v) => {
            const c = v / 255;
            return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
        };
        return (0.2126 * canal(parseInt(hex.slice(1, 3), 16)) +
            0.7152 * canal(parseInt(hex.slice(3, 5), 16)) +
            0.0722 * canal(parseInt(hex.slice(5, 7), 16)));
    };
    const a = luminance(avant);
    const b = luminance(arriere);
    return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}
//# sourceMappingURL=index.js.map